import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getDepartment, getForms, getSubmissions } from '@/lib/api';
import { FileText, Eye, BarChart3, ExternalLink, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  live: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-slate-100 text-slate-600'
};

const statusLabels = {
  draft: 'Entwurf',
  pending_approval: 'Ausstehend',
  approved: 'Genehmigt',
  live: 'Live',
  archived: 'Archiviert'
};

const DepartmentDashboard = () => {
  const { id } = useParams();
  const { user, isSuperAdmin } = useAuth();
  const [department, setDepartment] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [deptData, formsData] = await Promise.all([
        getDepartment(id),
        getForms()
      ]);
      
      setDepartment(deptData);
      
      // Filter forms for this department
      const deptForms = formsData.filter(f => f.department_id === id);
      setForms(deptForms);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Check access
  const hasAccess = () => {
    if (isSuperAdmin()) return true;
    if (user?.department_id === id) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!hasAccess()) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Sie haben keinen Zugriff auf diese Abteilung.</p>
        <Link to="/admin/dashboard">
          <Button variant="outline" className="mt-4">Zurück zum Dashboard</Button>
        </Link>
      </div>
    );
  }

  const activeForms = forms.filter(f => f.status === 'live');
  const draftForms = forms.filter(f => f.status === 'draft');
  const totalSubmissions = forms.reduce((sum, f) => sum + (f.submission_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/departments">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{department?.name}</h1>
          <p className="text-gray-500 mt-1">Abteilungs-Dashboard</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Live Formulare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{activeForms.length}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Entwürfe</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">{draftForms.length}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Gesamt Formulare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{forms.length}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Einreichungen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{totalSubmissions}</div>
          </CardContent>
        </Card>
      </div>

      {/* Forms Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Formulare</CardTitle>
          <CardDescription>Alle Formulare dieser Abteilung</CardDescription>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Keine Formulare in dieser Abteilung</p>
              <Link to="/admin/forms/new">
                <Button className="mt-4">Formular erstellen</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forms.map(form => (
                <Card key={form.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base font-semibold line-clamp-1">
                        {form.name}
                      </CardTitle>
                      <Badge className={statusColors[form.status]}>
                        {statusLabels[form.status]}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 line-clamp-2 min-h-[40px]">
                      {form.description || 'Keine Beschreibung'}
                    </p>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <BarChart3 className="h-4 w-4" />
                        <span>{form.submission_count || 0} Einreichungen</span>
                      </div>
                      
                      <div className="flex gap-1">
                        {form.status === 'live' && (
                          <Link to={`/form/${form.slug}`} target="_blank">
                            <Button size="sm" variant="ghost" title="Formular öffnen">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        <Link to={`/admin/submissions?form_id=${form.id}`}>
                          <Button size="sm" variant="ghost" title="Einreichungen">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={`/admin/forms/${form.id}/edit`}>
                          <Button size="sm" variant="outline">Bearbeiten</Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DepartmentDashboard;
